import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Store as VendorIcon } from "@mui/icons-material";
import type { ReactNode } from "react";
import type { VMVendorSummary } from "@/services/vendorProfileService";
import { StatusBadge } from "./components/StatusBadge";
import type { VendorStatus } from "./types";

type Props = {
  vendors: VMVendorSummary[];
  onSelect: (vendorId: string) => void;
  title?: string;
  subtitle?: string;
  /** Rendered below the vendors card, inside the same page frame (ENG-27734). */
  below?: ReactNode;
};

export function VMVendorPicker({
  vendors,
  onSelect,
  below,
  title = "Your reviewed vendors",
  subtitle = "Vendors you onboarded. Select one to view their profile.",
}: Props) {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 3 }}>
        <Paper
          variant="outlined"
          sx={{
            borderColor: "#E0E4EF",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow:
              "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 4,
              py: 3,
              borderBottom: 1,
              borderColor: "#E0E4EF",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1,
                bgcolor: "primary.light",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VendorIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            </Box>
          </Box>

          {vendors.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Typography sx={{ color: "text.secondary" }}>
                No vendors under your review yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vendor Legal Name</TableCell>
                    <TableCell>Primary GSTIN</TableCell>
                    <TableCell>Primary City</TableCell>
                    <TableCell align="center">Facilities</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendors.map((v) => (
                    <TableRow
                      key={v.vendor_id}
                      hover
                      onClick={() => onSelect(v.vendor_id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {v.legal_name}
                      </TableCell>
                      <TableCell>{v.primary_gst ?? "—"}</TableCell>
                      <TableCell>{v.primary_city ?? "—"}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={v.facilities_count}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={v.status as VendorStatus} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
        {below}
      </Box>
    </Box>
  );
}

export default VMVendorPicker;
